import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useDebounce } from '@/hooks/use-debounce';
import { useColocApi } from '@/hooks/use-coloc-api';
import { Contract } from '../../types';
import { queryClient } from '@/lib/react-query';
import { getContractsList } from '@/services/coloc-api/admin';
import { useContractsListFiltersContext } from '../use-get-contracts-list-filter';
import { ContractsListSortType, contractsListSortableIds } from '@/lib/coloc-api/admin';
import { useContractsSortingContext } from '../use-contracts-sorting-context';

interface GetContractsListResponse {
  pagination: {
    page: number;
    page_limit: number;
    total: number;
    total_pages: number;
  };
  data: Contract[];
}

const useGetContractsList = () => {
  const {
    searchQuery,
    status,
    moveInStartDate,
    moveInEndDate,
    moveOutStartDate,
    moveOutEndDate,
    residencyRegistrationStatus,
    propertyManagerId: selectedPropertyManagerId,
    roomNumber
  } = useContractsListFiltersContext()!;

  const {
    setPage,
    setPageLimit,
    page,
    pageLimit,
    resultSummary,
    setPaginationResponse,
  } = useColocApi<ContractsListSortType>()!;

  const {
    sortDirection,
    setSortDirection,
    selectedColumnToSort,
    setColumnToSort,
  } = useContractsSortingContext();

  const debouncedSearchQuery: string = useDebounce(searchQuery ?? '', 500);

  const formatDateRange = (startDate?: Date, endDate?: Date) => {
    if (!startDate || !endDate) return undefined;
    return `${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`;
  };

  const moveInDateRange = formatDateRange(moveInStartDate, moveInEndDate);
  const moveOutDateRange = formatDateRange(moveOutStartDate, moveOutEndDate);

  const queryKey: QueryKey = useMemo(
    () => [
      'getContractsList',
      status?.slug,
      moveInDateRange,
      moveOutDateRange,
      debouncedSearchQuery,
      searchQuery,
      pageLimit,
      page,
      selectedPropertyManagerId,
      residencyRegistrationStatus?.slug,
      selectedColumnToSort,
      sortDirection,
      roomNumber
    ],
    [
      debouncedSearchQuery,
      moveOutDateRange,
      moveInDateRange,
      page,
      pageLimit,
      searchQuery,
      status,
      selectedPropertyManagerId,
      residencyRegistrationStatus,
      selectedColumnToSort,
      sortDirection,
      roomNumber
    ]
  );

  const fetch = useQuery({
    queryKey,
    queryFn: () =>
      getContractsList({
        ...(moveInDateRange && { move_in: moveInDateRange }),
        ...(moveOutDateRange && { move_out: moveOutDateRange }),
        ...(pageLimit && { page_limit: pageLimit }),
        ...(page && { page }),
        ...(debouncedSearchQuery && { query: debouncedSearchQuery }),
        ...(status && { status: status.slug }),
        ...(selectedPropertyManagerId && {
          property_manager_id: selectedPropertyManagerId,
        }),
        ...(residencyRegistrationStatus && {
          residency_registration_status: residencyRegistrationStatus.slug,
        }),
        ...(selectedColumnToSort && { sort: selectedColumnToSort }),
        ...(sortDirection && { sort_direction: sortDirection }),
        ...(roomNumber && { room_number: roomNumber }),
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    setPaginationResponse(
      fetch.data?.data.pagination ? { pagination: fetch.data?.data.pagination } : undefined
    );
  }, [fetch.data?.data.pagination, setPaginationResponse]);

  const updateContractInCache = useCallback(
    (contract: Contract) => {
      queryClient.setQueryData(
        queryKey,
        (oldData: AxiosResponse<GetContractsListResponse> | undefined) => {
          if (oldData?.data) {
            const oldDataCopy = { ...oldData };
            const newData = oldDataCopy.data.data.map((mappedContract) => {
              if (mappedContract.id === contract.id) {
                return contract;
              }
              return mappedContract;
            });
            oldDataCopy.data.data = newData;
            return oldDataCopy;
          }
          return oldData;
        }
      );
    },
    [queryKey]
  );

  return {
    contracts: fetch.data?.data.data ?? [],
    pageLimit,
    setPageLimit,
    fetch,
    moveInDateRange,
    moveOutDateRange,
    searchQuery,
    setPage,
    resultSummary,
    updateContractInCache,
    isLoading: fetch.isLoading,
    sortableColumns: contractsListSortableIds,
    sortDirection,
    setSortDirection,
    selectedColumnToSort,
    setColumnToSort,
  };
};

const ContractsListContext = createContext<ReturnType<typeof useGetContractsList> | undefined>(
  undefined
);

const ContractsListProvider = ({ children }: { children: React.ReactNode }) => {
  const hook = useGetContractsList();

  return <ContractsListContext.Provider value={hook}>{children}</ContractsListContext.Provider>;
};

const useContractsListContext = () => {
  const context = useContext(ContractsListContext);
  if (context === undefined) {
    throw new Error('useContractsListContext must be used within a ContractsListProvider');
  }

  return context;
};

export { ContractsListProvider, useContractsListContext };
